import { useCallback, useEffect, useRef, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $isElementNode,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
  $createParagraphNode,
} from "lexical";
import { $setBlocksType } from "@lexical/selection";
import { $createHeadingNode, $createQuoteNode, $isHeadingNode } from "@lexical/rich-text";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  $isListNode,
  ListNode,
} from "@lexical/list";
import { $getNearestNodeOfType } from "@lexical/utils";
import { getDOMRangeRect } from "./utils/getDOMRangeRect";
import styles from "./FloatingToolbar.module.css";

type BlockType = "paragraph" | "h1" | "h2" | "h3" | "quote" | "bullet" | "number";

export function FloatingToolbar() {
  const [editor] = useLexicalComposerContext();
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>("paragraph");

  const updatePosition = useCallback(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    const nativeSelection = window.getSelection();
    if (!nativeSelection || nativeSelection.rangeCount === 0 || nativeSelection.isCollapsed) {
      setHasSelection(false);
      return;
    }

    const range = nativeSelection.getRangeAt(0);
    const rootElement = editor.getRootElement();
    if (!rootElement || !rootElement.contains(range.commonAncestorContainer)) {
      setHasSelection(false);
      return;
    }

    const rect = getDOMRangeRect(range, rootElement);
    const toolbarRect = toolbar.getBoundingClientRect();

    // Position above the selection, centered
    const top = rect.top - toolbarRect.height - 8;
    const left = rect.left + rect.width / 2 - toolbarRect.width / 2;

    // Clamp to viewport
    const clampedLeft = Math.max(8, Math.min(left, window.innerWidth - toolbarRect.width - 8));
    const clampedTop = top < 8 ? rect.bottom + 8 : top;

    toolbar.style.top = `${clampedTop}px`;
    toolbar.style.left = `${clampedLeft}px`;
  }, [editor]);

  const updateToolbarState = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
      setHasSelection(false);
      return;
    }

    setHasSelection(true);
    setIsBold(selection.hasFormat("bold"));
    setIsItalic(selection.hasFormat("italic"));
    setIsUnderline(selection.hasFormat("underline"));

    const anchorNode = selection.anchor.getNode();
    const element =
      anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();

    if ($isHeadingNode(element)) {
      setBlockType(element.getTag() as BlockType);
    } else if ($isListNode(element)) {
      const listType = element.getListType();
      setBlockType(listType === "number" ? "number" : "bullet");
    } else if ($isElementNode(element)) {
      const listNode = $getNearestNodeOfType(anchorNode, ListNode);
      if (listNode) {
        const listType = listNode.getListType();
        setBlockType(listType === "number" ? "number" : "bullet");
      } else {
        const type = element.getType();
        setBlockType(type === "quote" ? "quote" : "paragraph");
      }
    } else {
      setBlockType("paragraph");
    }

    // Defer position update to after DOM paint
    requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbarState();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, updateToolbarState]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbarState();
      });
    });
  }, [editor, updateToolbarState]);

  // Hide on scroll
  useEffect(() => {
    const handleScroll = () => setHasSelection(false);
    window.addEventListener("scroll", handleScroll, true);
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, []);

  const formatBold = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold");
  const formatItalic = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic");
  const formatUnderline = () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline");

  const formatParagraph = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const formatHeading = (headingSize: "h1" | "h2" | "h3") => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === headingSize) {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === "quote") {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      }
    });
  };

  const formatBulletList = () => {
    if (blockType !== "bullet") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "number") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      formatParagraph();
    }
  };

  // Position toolbar after it mounts
  useEffect(() => {
    if (hasSelection) {
      requestAnimationFrame(() => updatePosition());
    }
  }, [hasSelection, updatePosition]);

  if (!hasSelection) return null;

  return (
    <div
      ref={toolbarRef}
      className={styles.floatingToolbar}
      role="toolbar"
      aria-label="Floating formatting toolbar"
      style={{ position: "fixed" }}
    >
      <button
        type="button"
        className={`${styles.button} ${isBold ? styles.active : ""}`}
        onClick={formatBold}
        aria-label="Bold"
        title="Bold (Ctrl+B)"
      >
        <BoldIcon />
      </button>
      <button
        type="button"
        className={`${styles.button} ${isItalic ? styles.active : ""}`}
        onClick={formatItalic}
        aria-label="Italic"
        title="Italic (Ctrl+I)"
      >
        <ItalicIcon />
      </button>
      <button
        type="button"
        className={`${styles.button} ${isUnderline ? styles.active : ""}`}
        onClick={formatUnderline}
        aria-label="Underline"
        title="Underline (Ctrl+U)"
      >
        <UnderlineIcon />
      </button>

      <div className={styles.divider} />

      <button
        type="button"
        className={`${styles.button} ${blockType === "h1" ? styles.active : ""}`}
        onClick={() => formatHeading("h1")}
        aria-label="Heading 1"
        title="Heading 1"
      >
        H1
      </button>
      <button
        type="button"
        className={`${styles.button} ${blockType === "h2" ? styles.active : ""}`}
        onClick={() => formatHeading("h2")}
        aria-label="Heading 2"
        title="Heading 2"
      >
        H2
      </button>
      <button
        type="button"
        className={`${styles.button} ${blockType === "h3" ? styles.active : ""}`}
        onClick={() => formatHeading("h3")}
        aria-label="Heading 3"
        title="Heading 3"
      >
        H3
      </button>

      <div className={styles.divider} />

      <button
        type="button"
        className={`${styles.button} ${blockType === "bullet" ? styles.active : ""}`}
        onClick={formatBulletList}
        aria-label="Bullet List"
        title="Bullet List"
      >
        <BulletListIcon />
      </button>
      <button
        type="button"
        className={`${styles.button} ${blockType === "number" ? styles.active : ""}`}
        onClick={formatNumberedList}
        aria-label="Numbered List"
        title="Numbered List"
      >
        <NumberedListIcon />
      </button>
      <button
        type="button"
        className={`${styles.button} ${blockType === "quote" ? styles.active : ""}`}
        onClick={formatQuote}
        aria-label="Quote"
        title="Quote"
      >
        <QuoteIcon />
      </button>
    </div>
  );
}

function BoldIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 2.5H9C9.92826 2.5 10.8185 2.86875 11.4749 3.52513C12.1313 4.1815 12.5 5.07174 12.5 6C12.5 6.92826 12.1313 7.8185 11.4749 8.47487C10.8185 9.13125 9.92826 9.5 9 9.5H4V2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 9.5H10C10.9283 9.5 11.8185 9.86875 12.4749 10.5251C13.1313 11.1815 13.5 12.0717 13.5 13C13.5 13.9283 13.1313 14.8185 12.4749 15.4749C11.8185 16.1313 10.9283 16.5 10 16.5H4V9.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        transform="translate(0, -3.5)"
      />
    </svg>
  );
}

function ItalicIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line
        x1="9"
        y1="3"
        x2="7"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="6"
        y1="3"
        x2="11"
        y2="3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="5"
        y1="13"
        x2="10"
        y2="13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function BulletListIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="3" cy="4" r="1.5" fill="currentColor" />
      <circle cx="3" cy="8" r="1.5" fill="currentColor" />
      <circle cx="3" cy="12" r="1.5" fill="currentColor" />
      <line
        x1="7"
        y1="4"
        x2="14"
        y2="4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="8"
        x2="14"
        y2="8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="12"
        x2="14"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function NumberedListIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <text x="2" y="5.5" fontSize="5" fill="currentColor" fontFamily="var(--font-mono)">
        1.
      </text>
      <text x="2" y="9.5" fontSize="5" fill="currentColor" fontFamily="var(--font-mono)">
        2.
      </text>
      <text x="2" y="13.5" fontSize="5" fill="currentColor" fontFamily="var(--font-mono)">
        3.
      </text>
      <line
        x1="7"
        y1="4"
        x2="14"
        y2="4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="8"
        x2="14"
        y2="8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <line
        x1="7"
        y1="12"
        x2="14"
        y2="12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function QuoteIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 8C3 6.34315 4.34315 5 6 5V5C6 6.65685 4.65685 8 3 8V8V10C3 10.5523 3.44772 11 4 11H5C5.55228 11 6 10.5523 6 10V8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 8C10 6.34315 11.3431 5 13 5V5C13 6.65685 11.6569 8 10 8V8V10C10 10.5523 10.4477 11 11 11H12C12.5523 11 13 10.5523 13 10V8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UnderlineIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 3V8C4 9.06087 4.42143 10.0783 5.17157 10.8284C5.92172 11.5786 6.93913 12 8 12C9.06087 12 10.0783 11.5786 10.8284 10.8284C11.5786 10.0783 12 9.06087 12 8V3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line
        x1="3"
        y1="14"
        x2="13"
        y2="14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
